import { useEffect, useMemo, useState } from "react";
import { Wrench, Search, Plus, Edit2, Trash2, X, Phone, Calendar, DollarSign, User, Car, AlertCircle, CheckCircle2, Clock, ChevronRight, Truck, Camera, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { formatCurrency } from "@/lib/api";

const STATUS_META = {
  open:        { label: "Aberto",       cls: "border-warning text-warning bg-warning/10",        icon: AlertCircle },
  in_progress: { label: "Em andamento", cls: "border-blue-500 text-blue-400 bg-blue-500/10",     icon: Clock },
  done:        { label: "Concluído",    cls: "border-success text-success bg-success/10",        icon: CheckCircle2 },
};

const STATUS_FLOW = ["open", "in_progress", "done"];

async function uploadCheckinPhoto(file) {
  if (!file) throw new Error("no file");
  if (file.size > 10 * 1024 * 1024) throw new Error("Foto muito grande (máx 10MB)");
  const sig = (await api.get("/cloudinary/signature", { params: { folder: "post_sales/" } })).data;
  const fd = new FormData();
  fd.append("file", file);
  fd.append("api_key", sig.api_key);
  fd.append("timestamp", sig.timestamp);
  fd.append("signature", sig.signature);
  fd.append("folder", sig.folder);
  const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, { method: "POST", body: fd });
  const json = await res.json();
  if (!json.secure_url) throw new Error(json.error?.message || "Falha no upload");
  return { url: json.secure_url, public_id: json.public_id };
}

async function openCheckinPdf(id) {
  const r = await api.get(`/post-sales/${id}/checkin-pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
  window.open(url, "_blank");
}

export default function PostSales({ t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all"); // all | revisao | garantia
  const [editing, setEditing] = useState(null); // null | "new" | item
  const [prefillCar, setPrefillCar] = useState(null); // car data injected when "new from delivered" used
  const [pickerOpen, setPickerOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/post-sales");
      setItems(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error_generic"));
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const counts = useMemo(() => ({
    all: items.length,
    open: items.filter(x => x.status === "open").length,
    in_progress: items.filter(x => x.status === "in_progress").length,
    done: items.filter(x => x.status === "done").length,
    revisao: items.filter(x => x.kind === "revisao").length,
    garantia: items.filter(x => (x.kind || "garantia") === "garantia").length,
  }), [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(x => {
      if (statusFilter !== "all" && x.status !== statusFilter) return false;
      if (kindFilter !== "all" && (x.kind || "garantia") !== kindFilter) return false;
      if (!q) return true;
      return `${x.vin} ${x.make} ${x.model} ${x.customer_name} ${x.problem} ${x.work_to_do}`.toLowerCase().includes(q);
    });
  }, [items, search, statusFilter, kindFilter]);

  const totalCost = useMemo(() => filtered.reduce((s, x) => s + Number(x.cost || 0), 0), [filtered]);
  const openCount = counts.open + counts.in_progress;

  const advance = async (item) => {
    const idx = STATUS_FLOW.indexOf(item.status);
    const next = STATUS_FLOW[Math.min(idx + 1, STATUS_FLOW.length - 1)];
    if (next === item.status) return;
    try {
      await api.put(`/post-sales/${item.id}`, { ...item, status: next });
      toast.success("Status atualizado");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || t("error_generic")); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este reparo?")) return;
    try {
      await api.delete(`/post-sales/${id}`);
      toast.success("Removido");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || t("error_generic")); }
  };

  return (
    <div data-testid="post-sales-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="label-eyebrow text-primary mb-2">Pós-Vendas</p>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Pós-Vendas</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="add-post-sale-from-delivered"
            onClick={() => setPickerOpen(true)}
            className="border border-success text-success hover:bg-success hover:text-white px-5 py-3 font-display font-bold uppercase text-xs tracking-widest inline-flex items-center gap-2 transition-colors"
          >
            <Truck size={14} /> {t("post_sales_from_delivered") || "Carro entregue"}
          </button>
          <button
            data-testid="add-post-sale"
            onClick={() => { setPrefillCar(null); setEditing("new"); }}
            className="bg-primary hover:bg-primary-hover px-5 py-3 font-display font-bold uppercase text-xs tracking-widest inline-flex items-center gap-2"
          >
            <Plus size={14} /> Novo reparo
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <SummaryCard label="Em aberto" value={openCount} accent="text-warning border-warning/40" />
        <SummaryCard label="Concluídos" value={counts.done} accent="text-success border-success/40" />
        <SummaryCard label="Total de reparos" value={counts.all} />
        <SummaryCard label="Custo total" value={formatCurrency(totalCost)} accent="text-primary border-primary" big />
      </div>

      {/* Search + status filter */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="border border-border flex items-center px-4 h-12 flex-1 min-w-[280px]">
          <Search size={16} className="text-text-secondary mr-3" />
          <input
            data-testid="post-sales-search"
            type="text"
            placeholder="Buscar VIN, cliente, marca, problema..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-transparent w-full focus:outline-none text-sm"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {[
          { id: "all", label: `Todos os tipos (${counts.all})` },
          { id: "revisao", label: `🔧 Revisão de entrega (${counts.revisao})` },
          { id: "garantia", label: `🛡️ Pós-venda / Garantia (${counts.garantia})` },
        ].map(s => (
          <button
            key={s.id}
            data-testid={`ps-kind-${s.id}`}
            onClick={() => setKindFilter(s.id)}
            className={`px-4 py-2 text-xs font-display font-bold uppercase tracking-widest border transition-colors ${
              kindFilter === s.id ? "bg-[#C9A227] border-[#C9A227] text-black" : "border-border text-text-secondary hover:border-[#C9A227] hover:text-[#C9A227]"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: "all", label: `Todos (${counts.all})` },
          { id: "open", label: `Aberto (${counts.open})` },
          { id: "in_progress", label: `Em andamento (${counts.in_progress})` },
          { id: "done", label: `Concluído (${counts.done})` },
        ].map(s => (
          <button
            key={s.id}
            data-testid={`ps-filter-${s.id}`}
            onClick={() => setStatusFilter(s.id)}
            className={`px-4 py-2 text-xs font-display font-bold uppercase tracking-widest border transition-colors ${
              statusFilter === s.id ? "bg-primary border-primary text-white" : "border-border text-text-secondary hover:border-primary hover:text-primary"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-text-secondary text-center py-16 border border-dashed border-border">Carregando...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border">
          <Wrench size={32} className="mx-auto text-text-secondary mb-4" />
          <p className="text-text-secondary">Nenhum reparo cadastrado ainda.</p>
          <button
            data-testid="empty-add-post-sale"
            onClick={() => setEditing("new")}
            className="mt-4 text-primary text-xs font-display font-bold uppercase tracking-widest hover:underline"
          >
            + Adicionar o primeiro
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map(it => (
            <RepairCard key={it.id} item={it} onEdit={() => setEditing(it)} onDelete={() => onDelete(it.id)} onAdvance={() => advance(it)} />
          ))}
        </div>
      )}

      {editing && (
        <RepairForm
          item={editing === "new" ? null : editing}
          prefillCar={prefillCar}
          onClose={() => { setEditing(null); setPrefillCar(null); }}
          onSaved={() => { setEditing(null); setPrefillCar(null); reload(); }}
        />
      )}

      {pickerOpen && (
        <DeliveredCarPicker
          t={t}
          onClose={() => setPickerOpen(false)}
          onPick={(car) => {
            setPrefillCar(car);
            setPickerOpen(false);
            setEditing("new");
          }}
        />
      )}
    </div>
  );
}

function SummaryCard({ label, value, accent = "", big = false }) {
  return (
    <div className={`border ${accent.includes("border") ? accent : "border-border"} bg-surface p-4`}>
      <p className="label-eyebrow text-text-secondary mb-1">{label}</p>
      <p className={`font-display font-black ${big ? "text-2xl" : "text-3xl"} ${accent.includes("text") ? accent.split(" ").find(c => c.startsWith("text-")) : ""}`}>{value}</p>
    </div>
  );
}

function RepairCard({ item, onEdit, onDelete, onAdvance }) {
  const meta = STATUS_META[item.status] || STATUS_META.open;
  const Icon = meta.icon;
  const canAdvance = item.status !== "done";

  return (
    <div data-testid={`ps-card-${item.id}`} className="border border-border bg-surface p-5 hover:border-primary/60 transition-colors">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] font-display font-bold uppercase tracking-wider ${(item.kind || "garantia") === "revisao" ? "border-[#C9A227]/60 text-[#C9A227]" : "border-primary/60 text-primary"}`}>
              {(item.kind || "garantia") === "revisao" ? "🔧 Revisão de entrega" : "🛡️ Garantia"}
            </span>
            <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] font-display font-bold uppercase tracking-wider ${meta.cls}`}>
              <Icon size={10} /> {meta.label}
            </span>
            {item.vin && (
              <span className="text-[10px] uppercase tracking-widest text-text-secondary border border-border px-2 py-0.5">VIN: {item.vin}</span>
            )}
          </div>
          <p className="font-display font-bold text-lg uppercase truncate">
            {[item.year, item.make, item.model].filter(Boolean).join(" ") || "Carro sem identificação"}
          </p>
          {item.color && <p className="text-xs text-text-secondary">{item.color}</p>}
        </div>
        <div className="flex items-center gap-1">
          <button
            data-testid={`ps-pdf-${item.id}`}
            onClick={async () => { try { await openCheckinPdf(item.id); } catch { /* toast below */ } }}
            title="Comprovante de entrada (PDF)"
            className="border border-gold text-[#C9A227] border-[#C9A227]/60 hover:bg-[#C9A227] hover:text-black p-2 transition-colors"
          >
            <FileText size={14} />
          </button>
          {canAdvance && (
            <button
              data-testid={`ps-advance-${item.id}`}
              onClick={onAdvance}
              title="Avançar status"
              className="border border-primary text-primary hover:bg-primary hover:text-white p-2 transition-colors"
            >
              <ChevronRight size={14} />
            </button>
          )}
          <button data-testid={`ps-edit-${item.id}`} onClick={onEdit} title="Editar" className="border border-border hover:border-primary hover:text-primary p-2"><Edit2 size={14} /></button>
          <button data-testid={`ps-del-${item.id}`} onClick={onDelete} title="Excluir" className="border border-border hover:border-primary hover:text-primary p-2"><Trash2 size={14} /></button>
        </div>
      </div>

      {/* Customer */}
      {(item.customer_name || item.customer_phone) && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-secondary mb-3">
          {item.customer_name && <span className="inline-flex items-center gap-1.5"><User size={11} /> {item.customer_name}</span>}
          {item.customer_phone && <span className="inline-flex items-center gap-1.5"><Phone size={11} /> {item.customer_phone}</span>}
        </div>
      )}

      {/* Problem & work */}
      {item.problem && (
        <div className="mb-2">
          <p className="label-eyebrow text-text-secondary mb-1">Problema</p>
          <p className="text-sm">{item.problem}</p>
        </div>
      )}
      {item.work_to_do && (
        <div className="mb-3">
          <p className="label-eyebrow text-text-secondary mb-1">A fazer</p>
          <p className="text-sm">{item.work_to_do}</p>
        </div>
      )}

      {/* Footer row */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-border text-xs">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-text-secondary">
          {item.entry_date && <span className="inline-flex items-center gap-1"><Calendar size={11} /> Entrada {item.entry_date}{item.entry_time ? ` às ${item.entry_time}` : ""}</span>}
          {item.exit_date && <span className="inline-flex items-center gap-1"><Calendar size={11} /> Saída {item.exit_date}</span>}
          {item.technician && <span className="inline-flex items-center gap-1"><Wrench size={11} /> {item.technician}</span>}
          {(item.photos || []).length > 0 && <span className="inline-flex items-center gap-1 text-[#C9A227]"><Camera size={11} /> {item.photos.length} foto{item.photos.length > 1 ? "s" : ""}</span>}
        </div>
        {Number(item.cost) > 0 && (
          <span className="inline-flex items-center gap-1 text-primary font-display font-bold">
            <DollarSign size={11} /> {formatCurrency(item.cost)}
          </span>
        )}
      </div>
    </div>
  );
}

function RepairForm({ item, prefillCar, onClose, onSaved }) {
  const isEdit = !!item;
  const [form, setForm] = useState(item || (prefillCar ? {
    vin: prefillCar.vin || "",
    vehicle_id: prefillCar.id || "",
    make: prefillCar.make || "",
    model: prefillCar.model || "",
    year: prefillCar.year || "",
    color: prefillCar.color || "",
    customer_name: prefillCar.buyer_name || "",
    customer_phone: prefillCar.buyer_phone || "",
    kind: "garantia",
    entry_date: new Date().toISOString().slice(0, 10),
    entry_time: new Date().toTimeString().slice(0, 5),
    mileage_in: "", photos: [],
    exit_date: "",
    problem: "", work_to_do: "",
    cost: 0, technician: "", notes: "",
    status: "open",
  } : {
    vin: "", vehicle_id: "", make: "", model: "", year: "", color: "",
    customer_name: "", customer_phone: "",
    kind: "garantia",
    entry_date: new Date().toISOString().slice(0, 10),
    entry_time: new Date().toTimeString().slice(0, 5),
    mileage_in: "", photos: [],
    exit_date: "",
    problem: "", work_to_do: "",
    cost: 0, technician: "", notes: "",
    status: "open",
  }));
  const [lookupBusy, setLookupBusy] = useState(false);
  const [foundCar, setFoundCar] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const photos = form.photos || [];

  const addPhotos = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    if (photos.length + list.length > 10) {
      toast.error("Máximo de 10 fotos por entrada");
      return;
    }
    setUploading(true);
    try {
      for (const f of list) {
        const up = await uploadCheckinPhoto(f);
        setForm((fm) => ({ ...fm, photos: [...(fm.photos || []), up] }));
      }
      toast.success("Foto(s) adicionada(s)");
    } catch (e) {
      toast.error(e.message || "Falha no upload da foto");
    } finally { setUploading(false); }
  };

  const removePhoto = (idx) => setForm((fm) => ({ ...fm, photos: (fm.photos || []).filter((_, i) => i !== idx) }));

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const lookupVin = async () => {
    const vin = (form.vin || "").trim();
    if (!vin) { toast.error("Digite o VIN primeiro"); return; }
    setLookupBusy(true);
    try {
      const r = await api.get("/post-sales/lookup-vin", { params: { vin } });
      if (r.data?.found) {
        setForm(f => ({
          ...f,
          vehicle_id: r.data.vehicle_id || "",
          vin: r.data.vin || vin,
          make: r.data.make || f.make,
          model: r.data.model || f.model,
          year: r.data.year || f.year,
          color: r.data.color || f.color,
          customer_name: r.data.customer_name || f.customer_name,
          customer_phone: r.data.customer_phone || f.customer_phone,
        }));
        setFoundCar(r.data);
        toast.success("Carro encontrado, dados preenchidos");
      } else {
        setFoundCar(null);
        toast.message("VIN não encontrado", { description: "Você pode preencher manualmente os dados do carro abaixo." });
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erro na busca"); }
    finally { setLookupBusy(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      year: form.year ? Number(form.year) : null,
      cost: Number(form.cost || 0),
    };
    try {
      if (isEdit) await api.put(`/post-sales/${item.id}`, payload);
      else await api.post("/post-sales", payload);
      toast.success("Salvo!");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-auto py-8 px-4">
      <form onSubmit={save} className="bg-background border border-border w-full max-w-3xl p-7 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-black text-2xl uppercase tracking-tight">
            {isEdit ? "Editar reparo" : "Novo reparo"}
          </h2>
          <button type="button" onClick={onClose}><X size={20} className="text-text-secondary hover:text-primary" /></button>
        </div>

        {/* VIN lookup */}
        <div className="border border-border p-4 bg-surface space-y-3">
          <div>
            <label className="label-eyebrow block mb-2">VIN do veículo</label>
            <div className="flex gap-2">
              <input
                data-testid="ps-form-vin"
                type="text"
                value={form.vin}
                onChange={(e) => set("vin", e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookupVin(); } }}
                placeholder="Digite o VIN..."
                className="flex-1 bg-background border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm uppercase tracking-wider"
              />
              <button
                type="button"
                data-testid="ps-form-lookup-btn"
                onClick={lookupVin}
                disabled={lookupBusy}
                className="border border-primary text-primary hover:bg-primary hover:text-white px-5 h-11 font-display font-bold uppercase text-xs tracking-widest disabled:opacity-50"
              >
                {lookupBusy ? "Buscando..." : "Buscar"}
              </button>
            </div>
          </div>
          {foundCar && (
            <div className="flex items-center gap-3 text-xs text-success border border-success/40 bg-success/5 p-2.5">
              <Car size={14} />
              <span className="font-display font-bold uppercase tracking-wider">Carro encontrado:</span>
              <span>{foundCar.year} {foundCar.make} {foundCar.model}</span>
              {foundCar.customer_name && <span className="text-text-secondary">· Cliente: {foundCar.customer_name}</span>}
            </div>
          )}
        </div>

        {/* Vehicle data */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <FormInput label="Marca" value={form.make} onChange={(v) => set("make", v)} testid="ps-form-make" />
          <FormInput label="Modelo" value={form.model} onChange={(v) => set("model", v)} testid="ps-form-model" />
          <FormInput label="Ano" type="number" value={form.year || ""} onChange={(v) => set("year", v)} testid="ps-form-year" />
          <FormInput label="Cor" value={form.color} onChange={(v) => set("color", v)} testid="ps-form-color" />
        </div>

        {/* Customer */}
        <div className="grid grid-cols-2 gap-3">
          <FormInput label="Cliente" value={form.customer_name} onChange={(v) => set("customer_name", v)} testid="ps-form-customer" />
          <FormInput label="Telefone do cliente" value={form.customer_phone} onChange={(v) => set("customer_phone", v)} testid="ps-form-phone" />
        </div>

        {/* Problem & work */}
        <div className="space-y-3">
          <div>
            <label className="label-eyebrow block mb-2">Problema relatado</label>
            <textarea
              data-testid="ps-form-problem"
              value={form.problem}
              onChange={(e) => set("problem", e.target.value)}
              rows={2}
              placeholder="Ex: barulho no motor, ar não gela..."
              className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 py-2 text-sm resize-none"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">O que precisa ser feito</label>
            <textarea
              data-testid="ps-form-work"
              value={form.work_to_do}
              onChange={(e) => set("work_to_do", e.target.value)}
              rows={3}
              placeholder="Ex: trocar correia dentada, recarregar gás do ar..."
              className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 py-2 text-sm resize-none"
            />
          </div>
        </div>

        {/* Tipo do registro */}
        <div className="flex flex-wrap gap-2">
          {[
            { id: "garantia", label: "🛡️ Pós-venda / Garantia (cliente trouxe de volta)" },
            { id: "revisao", label: "🔧 Revisão de entrega (preparação do carro vendido)" },
          ].map(k => (
            <button
              key={k.id}
              type="button"
              data-testid={`ps-form-kind-${k.id}`}
              onClick={() => set("kind", k.id)}
              className={`px-4 py-2 text-xs font-display font-bold uppercase tracking-wide border transition-colors ${
                (form.kind || "garantia") === k.id ? "bg-[#C9A227] border-[#C9A227] text-black" : "border-border text-text-secondary hover:border-[#C9A227]"
              }`}
            >
              {k.label}
            </button>
          ))}
        </div>

        {/* Dates / cost / status / tech */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <FormInput label="Data de entrada" type="date" value={form.entry_date || ""} onChange={(v) => set("entry_date", v)} testid="ps-form-entry" />
          <FormInput label="Hora da entrada" type="time" value={form.entry_time || ""} onChange={(v) => set("entry_time", v)} testid="ps-form-entry-time" />
          <FormInput label="Milhagem na entrada" value={form.mileage_in || ""} onChange={(v) => set("mileage_in", v)} testid="ps-form-mileage-in" />
          <FormInput label="Data de saída" type="date" value={form.exit_date || ""} onChange={(v) => set("exit_date", v)} testid="ps-form-exit" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <FormInput label="Custo" type="number" value={form.cost} onChange={(v) => set("cost", v)} testid="ps-form-cost" />
          <div>
            <label className="label-eyebrow block mb-2">Status</label>
            <select
              data-testid="ps-form-status"
              value={form.status}
              onChange={(e) => set("status", e.target.value)}
              className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
            >
              <option value="open">Aberto</option>
              <option value="in_progress">Em andamento</option>
              <option value="done">Concluído</option>
            </select>
          </div>
        </div>

        {/* Fotos do estado do carro na entrada */}
        <div className="border border-[#C9A227]/50 bg-surface p-4 space-y-3">
          <div className="flex items-center justify-between">
            <label className="label-eyebrow flex items-center gap-2 text-[#C9A227]"><Camera size={13} /> Fotos do estado do carro na entrada ({photos.length}/10)</label>
            <label className={`border border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227] hover:text-black px-4 h-9 inline-flex items-center font-display font-bold uppercase text-[10px] tracking-widest cursor-pointer transition-colors ${uploading || photos.length >= 10 ? "opacity-50 pointer-events-none" : ""}`}>
              {uploading ? "Enviando..." : "+ Adicionar fotos"}
              <input
                data-testid="ps-form-photos-input"
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="hidden"
                onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }}
              />
            </label>
          </div>
          <p className="text-[11px] text-text-secondary">Tire ~10 fotos (rodas, para-choques, laterais, teto, interior) — elas entram no comprovante PDF e protegem a loja de "já estava arranhado".</p>
          {photos.length > 0 && (
            <div className="grid grid-cols-5 gap-2">
              {photos.map((p, i) => (
                <div key={p.public_id || i} className="relative group border border-border">
                  <img src={p.url} alt={`foto ${i + 1}`} className="w-full h-16 object-cover" />
                  <button
                    type="button"
                    data-testid={`ps-form-photo-del-${i}`}
                    onClick={() => removePhoto(i)}
                    className="absolute top-0.5 right-0.5 bg-black/70 text-white p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <FormInput label="Mecânico / responsável" value={form.technician} onChange={(v) => set("technician", v)} testid="ps-form-tech" />

        <div>
          <label className="label-eyebrow block mb-2">Observações</label>
          <textarea
            data-testid="ps-form-notes"
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            rows={2}
            className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 py-2 text-sm resize-none"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <button type="button" onClick={onClose} className="border border-border hover:border-primary hover:text-primary px-5 h-11 font-display font-bold uppercase text-xs tracking-widest">
            Cancelar
          </button>
          <button
            data-testid="ps-form-save"
            type="submit"
            disabled={saving}
            className="bg-primary hover:bg-primary-hover px-5 h-11 font-display font-bold uppercase text-xs tracking-widest disabled:opacity-50"
          >
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </form>
    </div>
  );
}

function FormInput({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
      />
    </div>
  );
}

function DeliveredCarPicker({ t, onClose, onPick }) {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/delivery");
        if (cancel) return;
        // Only step 8 = delivered.
        const delivered = (r.data || [])
          .filter(v => (v.delivery_step || 0) === 8)
          .sort((a, b) => (b.delivered_at || "").localeCompare(a.delivered_at || ""));
        setCars(delivered);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Erro ao carregar entregues");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return cars;
    return cars.filter(v => `${v.year || ""} ${v.make || ""} ${v.model || ""} ${v.vin || ""} ${v.buyer_name || ""}`.toLowerCase().includes(term));
  }, [cars, q]);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-auto py-8 px-4">
      <div className="bg-background border border-success w-full max-w-3xl p-7" data-testid="delivered-picker-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display font-black text-2xl uppercase tracking-tight">
              {t("post_sales_pick_delivered_title") || "Selecione o carro entregue"}
            </h2>
            <p className="text-xs text-text-secondary mt-1">
              {t("post_sales_pick_delivered_subtitle") || "Clique no carro e o ticket é criado com os dados preenchidos."}
            </p>
          </div>
          <button type="button" onClick={onClose} data-testid="delivered-picker-close">
            <X size={20} className="text-text-secondary hover:text-primary" />
          </button>
        </div>

        <div className="border border-border flex items-center px-4 h-12 mb-4">
          <Search size={16} className="text-text-secondary mr-3" />
          <input
            data-testid="delivered-picker-search"
            type="text"
            placeholder="Buscar por marca, modelo, VIN, cliente..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="bg-transparent w-full focus:outline-none text-sm"
          />
        </div>

        {loading ? (
          <p className="text-text-secondary text-center py-12 border border-dashed border-border">Carregando...</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border">
            <Truck size={32} className="mx-auto text-text-secondary mb-3" />
            <p className="text-text-secondary">{t("post_sales_no_delivered") || "Nenhum carro entregue ainda."}</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
            {filtered.map(v => (
              <button
                key={v.id}
                type="button"
                data-testid={`delivered-pick-${v.id}`}
                onClick={() => onPick(v)}
                className="w-full text-left border border-border bg-surface hover:border-success hover:bg-success/5 transition-colors p-3 flex items-center gap-4 group"
              >
                {v.images?.[0] ? (
                  <img src={v.images[0]} alt="" className="w-20 h-16 object-cover shrink-0" />
                ) : (
                  <div className="w-20 h-16 bg-background border border-border shrink-0 flex items-center justify-center">
                    <Car size={20} className="text-text-secondary" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold uppercase truncate text-sm">
                    {[v.year, v.make, v.model].filter(Boolean).join(" ")}
                  </p>
                  <p className="text-xs text-text-secondary truncate">
                    {v.buyer_name || "—"}
                    {v.vin && <span className="ml-2 uppercase tracking-wider">· VIN {v.vin.slice(-6)}</span>}
                  </p>
                  {v.delivered_at && (
                    <p className="text-[10px] text-text-secondary/70 mt-0.5">
                      Entregue em {new Date(v.delivered_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <ChevronRight size={16} className="text-text-secondary group-hover:text-success shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
